// Where an engine can be heard from: its listener positions and the impulse
// responses the voice is built with (docs/contracts/listeners.md).
//
// scripts/prebuild/25-listeners.mjs writes one table per engine data folder with
// a [listeners] table, and copies the IRs it names. The table lists the
// listener control's choices in index order, "direct" first; the IRs travel to
// the voice as assets of kind "ir", their id the path relative to data/ir/.

import type { AssetKind } from "./protocol";

/** One choice of the listener control. */
export interface ListenerChoice {
  id: string;
  name: string;
  description: string;
}

/** An engine's listeners (src/generated/listeners/<engine>.json). */
export interface ListenerTable {
  engine: string;
  /** The position heard when nobody chose one. */
  default: string;
  /** The listener control's choices, by index: "direct", then the positions. */
  listeners: ListenerChoice[];
  /** Every IR the positions name, by path relative to data/ir/. */
  irs: string[];
}

/** The host's listener 0: the stems as they are. */
export const DIRECT = "direct";

const tables = import.meta.glob<ListenerTable>("../generated/listeners/*.json", { eager: true, import: "default" });
const irUrls = import.meta.glob<string>("../generated/ir/**/*.wav", { query: "?url", import: "default" });

/** The listeners of an engine, or null for an engine that has none (it is heard direct). */
export function listenerTable(engine: string): ListenerTable | null {
  return tables[`../generated/listeners/${engine}.json`] ?? null;
}

/**
 * The listener control's index for `id`: 0 for "direct", 1 + the position's
 * index for a position. A null or unknown id gives the engine's default.
 */
export function listenerIndex(table: ListenerTable | null, id: string | null): number {
  if (table === null) return 0;
  const at = id === null ? -1 : table.listeners.findIndex((l) => l.id === id);
  if (at >= 0) return at;
  return Math.max(0, table.listeners.findIndex((l) => l.id === table.default));
}

/** An asset for the voice's start: an IR's id and its WAV bytes. */
export interface ListenerAsset {
  id: string;
  kind: AssetKind;
  bytes: ArrayBuffer;
}

const loaded = new Map<string, Promise<ArrayBuffer>>();

function fetchIr(path: string): Promise<ArrayBuffer> {
  let bytes = loaded.get(path);
  if (bytes === undefined) {
    const url = irUrls[`../generated/ir/${path}`];
    if (url === undefined) return Promise.reject(new Error(`no impulse response ${path} in the build`));
    bytes = url().then(async (href) => {
      const r = await fetch(href);
      if (!r.ok) throw new Error(`${href}: HTTP ${r.status}`);
      return r.arrayBuffer();
    });
    loaded.set(path, bytes);
  }
  return bytes;
}

/** Every IR an engine's listeners need, as assets for AudioEngine.start; none for an engine without listeners. */
export async function loadListenerAssets(engine: string): Promise<ListenerAsset[]> {
  const table = listenerTable(engine);
  if (table === null) return [];
  const bytes = await Promise.all(table.irs.map(fetchIr));
  return table.irs.map((id, i) => ({ id, kind: "ir" as const, bytes: bytes[i]! }));
}
